import { memo, useCallback, useEffect, useState } from 'react';
import cn from 'classnames';

import { Modal, RadioButton, SpinnerOverlay } from '@components/common';

import { useMutateUpdateAssetVisibility } from '@hooks/assets/assetVisibility/useMutateUpdateAssetVisibility';
import { useQueryAssetVisibility } from '@hooks/assets/assetVisibility/useQueryAssetVisibility';

import { EDefaultRestrictedAccess, TDefaultRestrictedAccess } from '@enums/common';

import { MODAL_WIDTH_1000 } from '@constants/modals';

import VisibilityMNOTable from './VisibilityMNOTable';

import styles from './VisibilitySettings.module.scss';

interface IVisibilitySettingsProps {
	organisationId: string;
}

const VisibilitySettings = ({ organisationId }: IVisibilitySettingsProps) => {
	const [
		activeRadioButton,
		setActiveRadioButton,
	] = useState<TDefaultRestrictedAccess>(EDefaultRestrictedAccess.default);
	const [isTableVisible, setIsTableVisible] = useState(false);

	const isRestricted = activeRadioButton === EDefaultRestrictedAccess.restricted;

	const {
		assetVisibility: { assetVisibilityCount = 0, assetVisibility } = {},
		isLoadingAssetVisibility,
	} = useQueryAssetVisibility({ id: organisationId });

	const {
		updateAssetVisibility,
		isLoadingUpdateAssetVisibility,
	} = useMutateUpdateAssetVisibility();

	const onChange = (name: TDefaultRestrictedAccess) => {
		updateAssetVisibility({ assetVisibility: name, id: organisationId });
		setActiveRadioButton(name);
	};

	const openTable = useCallback(() => {
		if (!isRestricted) {
			return;
		}

		setIsTableVisible(true);
	}, [isRestricted]);

	const closeTable = useCallback(() => setIsTableVisible(false), []);

	const actionClasses = cn(styles.action, {
		[styles.actionActive]: isRestricted,
		[styles.actionDisabled]: !isRestricted,
	});

	useEffect(() => {
		setActiveRadioButton(assetVisibility?.name || EDefaultRestrictedAccess.default);
	}, [assetVisibility?.name]);

	const isLoading = isLoadingAssetVisibility || isLoadingUpdateAssetVisibility || false;

	return (
		<div className={styles.container}>
			{isLoading && <SpinnerOverlay />}
			<Modal
				isOpen={isTableVisible}
				onClose={closeTable}
				title='Select MNOs'
				width={MODAL_WIDTH_1000}
			>
				<VisibilityMNOTable id={organisationId}/>
			</Modal>
			<div className={styles.title}>Visibility for MNOs</div>
			<div className={styles.btnContainer}>
				<RadioButton
					checked={activeRadioButton === EDefaultRestrictedAccess.default}
					id={EDefaultRestrictedAccess.default}
					infoText='Visible for all MNOs (default)'
					label='Visibility for MNOs'
					name={EDefaultRestrictedAccess.default}
					onChange={onChange}
					value={EDefaultRestrictedAccess.default}
				/>
			</div>
			<div className={styles.btnContainer}>
				<RadioButton
					checked={activeRadioButton === EDefaultRestrictedAccess.restricted}
					id={EDefaultRestrictedAccess.restricted}
					label='Restricted.'
					name={EDefaultRestrictedAccess.restricted}
					onChange={onChange}
					value={EDefaultRestrictedAccess.restricted}
				/>
				<div
					className={actionClasses}
					onClick={openTable}
				>
					Select MNOs {isRestricted && `(${assetVisibilityCount})`}
				</div>
			</div>
		</div>
	);
};

export default memo(VisibilitySettings);
